import { createReducer, on } from "@ngrx/store";
import { EditProcedureState, FactoryAsset, MappingAssetToProcedure, MappingAssetToResource, MergeArrayValues, Procedure, ProcedureInstance, ProcedureStepRedline, ProcedureVersion, Resource, SaveStatus, ScheduledProcedure, FreightCarrier, ViewProcedureState, Customer, Vendor, PurchaseRequestList, PurchaseRequest, Contact, SoftwareRequest, AssetHistoryEvent, PurchaseOrder, CapitalizationProject } from "@prism/prism-shared";
import { PrismCommonActions } from ".";
import { MetalsActions } from "../metals";
import { PlasticsActions } from "../plastics";

export interface PrismCommonState {
    resources: Resource[];

    procedures: Procedure[];
    procedureVersions: ProcedureVersion[];
    procedureInstances: ProcedureInstance[];
    procedureStepRedlines: ProcedureStepRedline[]
    scheduledProcedures: ScheduledProcedure[];

    factoryAssets: FactoryAsset[];
    assetHistoryEvents: AssetHistoryEvent[];

    mappingAssetToResources: MappingAssetToResource[];
    mappingAssetToProcedures: MappingAssetToProcedure[];

    customers: Customer[];
    vendors: Vendor[];
    contacts: Contact[];

    freightCarriers: FreightCarrier[]
    capitalizationProjects: CapitalizationProject[]

    purchaseRequestLists: PurchaseRequestList[];
    purchaseRequests: PurchaseRequest[];


    loadingState: number;
    error: any;

    viewProcedureState: ViewProcedureState;
    editProcedureState: EditProcedureState;

    softwareRequests: SoftwareRequest[];
}

export const initialState: PrismCommonState = {
    resources: [],

    procedures: [],
    procedureVersions: [],
    procedureInstances: [],
    procedureStepRedlines: [],
    scheduledProcedures: [],

    factoryAssets: [],
    assetHistoryEvents: [],

    mappingAssetToResources: [],
    mappingAssetToProcedures: [],

    customers: [],
    vendors: [],
    contacts: [],

    freightCarriers: [],
    capitalizationProjects: [],

    purchaseRequestLists: [],
    purchaseRequests: [],

    softwareRequests: [],

    loadingState: 0,
    error: null,

    viewProcedureState: {
        selectedProcedure: null as unknown as Procedure,
        selectedProcedureVersions: [],
        selectedProcedureVersionId: '',
    },

    editProcedureState: {
        procedure: null as unknown as Procedure,
        procedureVersion: null as unknown as ProcedureVersion,
        procedureStepRedlines: [],
        selectedRedlines: [],
        selectedRedlinePanelStepId: null,
        expandedRedlineIds: [],
        saveStatus: SaveStatus.Saved,
    },

}


export const prismCommonReducer = createReducer(
    initialState,
    // =========================================================
    on(PrismCommonActions.ResetState, (state): PrismCommonState => ({
        ...initialState,
    })),
    // =========================================================
    on(PrismCommonActions.LoadResources.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadResources.success, (state, { resources }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        resources: MergeArrayValues(state.resources, resources),
        error: null,
    })),
    on(PrismCommonActions.LoadResources.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadActiveResources.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadActiveResources.success, (state, { resources }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        resources,
        error: null,
    })),
    on(PrismCommonActions.LoadActiveResources.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetResource.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.SetResource.success, (state, { resource }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        resources: MergeArrayValues(state.resources, resource),
        error: null,
    })),
    on(PrismCommonActions.SetResource.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadResourceById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadResourceById.success, (state, { resource }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        resources: MergeArrayValues(state.resources, resource),
        error: null,
    })),
    on(PrismCommonActions.LoadResourceById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadResourcesByIds.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadResourcesByIds.success, (state, { resources }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        resources: MergeArrayValues(state.resources, resources),
        error: null,
    })),
    on(PrismCommonActions.LoadResourcesByIds.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadProcedures.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadProcedures.success, (state, { procedures }): PrismCommonState => ({
        ...state,
        procedures,
        loadingState: state.loadingState - 1,
        error: null,
    })),
    on(PrismCommonActions.LoadProcedures.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null,
    })),
    // =========================================================
    on(PrismCommonActions.LoadProceduresAndHighestReleasedVersion.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadProceduresAndHighestReleasedVersion.success, (state, { procedures, procedureVersions }): PrismCommonState => {
        return {
            ...state,
            loadingState: state.loadingState - 1,
            procedures: MergeArrayValues(state.procedures, procedures),
            procedureVersions: MergeArrayValues(state.procedureVersions, procedureVersions),
            error: null
        }
    }),
    on(PrismCommonActions.LoadProceduresAndHighestReleasedVersion.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadReleasedPlasticsProcedureVersions.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadReleasedPlasticsProcedureVersions.success, (state, { procedureVersions }): PrismCommonState => {
        return {
            ...state,
            loadingState: state.loadingState - 1,
            procedureVersions: MergeArrayValues(state.procedureVersions, procedureVersions),
            error: null
        }
    }),
    on(PrismCommonActions.LoadReleasedPlasticsProcedureVersions.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadCompletedProcedureInstancesWithLimit.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadCompletedProcedureInstancesWithLimit.success, (state, { instances }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureInstances: MergeArrayValues(state.procedureInstances, instances),
        error: null
    })),
    on(PrismCommonActions.LoadCompletedProcedureInstancesWithLimit.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadProcedureInstancesByStatuses.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadProcedureInstancesByStatuses.success, (state, { instances }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureInstances: MergeArrayValues(state.procedureInstances, instances),
        error: null
    })),
    on(PrismCommonActions.LoadProcedureInstancesByStatuses.set, (state, { instances }): PrismCommonState => ({
        ...state,
        procedureInstances: MergeArrayValues(state.procedureInstances, instances),
    })),
    on(PrismCommonActions.LoadProcedureInstancesByStatuses.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadProcedureVersionsByIds.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadProcedureVersionsByIds.success, (state, { procedureVersions }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureVersions: MergeArrayValues(state.procedureVersions, procedureVersions),
        error: null
    })),
    on(PrismCommonActions.LoadProcedureVersionsByIds.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.ViewProcedureLoadProcedureData.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.ViewProcedureLoadProcedureData.success, (state, {
        selectedProcedure,
        selectedProcedureVersionId,
        selectedProcedureVersions,
        scheduledProcedures,
        factoryAssets
    }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null,
        scheduledProcedures: MergeArrayValues(state.scheduledProcedures, scheduledProcedures),
        factoryAssets: MergeArrayValues(state.factoryAssets, factoryAssets),
        viewProcedureState: {
            ...state.viewProcedureState,
            selectedProcedure,
            selectedProcedureVersionId,
            selectedProcedureVersions,
        },
    })),
    on(PrismCommonActions.ViewProcedureLoadProcedureData.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetProcedureVersion.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetProcedureVersion.success, (state, { procedureVersion }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureVersions: MergeArrayValues(state.procedureVersions, procedureVersion),
        error: null
    })),
    on(PrismCommonActions.SetProcedureVersion.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetProcedure.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetProcedure.success, (state, { procedure }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedures: MergeArrayValues(state.procedures, procedure),
        error: null
    })),
    on(PrismCommonActions.SetProcedure.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.ViewProcedureUpdate.updateselectedprocdureversion, (state, { procedureVersion }): PrismCommonState => ({
        ...state,
        viewProcedureState: {
            ...state.viewProcedureState,
            selectedProcedureVersions: MergeArrayValues(state.viewProcedureState.selectedProcedureVersions, procedureVersion)
        }
    })),
    on(PrismCommonActions.ViewProcedureUpdate.updateselectedprocedure, (state, { procedure }): PrismCommonState => ({
        ...state,
        viewProcedureState: {
            ...state.viewProcedureState,
            selectedProcedure: procedure,
        }
    })),
    on(PrismCommonActions.ViewProcedureUpdate.setselectedprocedureversionid, (state, { procedureVersionId }): PrismCommonState => ({
        ...state,
        viewProcedureState: {
            ...state.viewProcedureState,
            selectedProcedureVersionId: procedureVersionId
        }
    })),
    // =========================================================
    on(PrismCommonActions.EditProcedureLoadProcedureData.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.EditProcedureLoadProcedureData.success, (state, {
        procedure,
        procedureVersion,
        redlines
    }): PrismCommonState => {
        return {
            ...state,
            loadingState: state.loadingState - 1,
            error: null,
            editProcedureState: {
                ...state.editProcedureState,
                procedure,
                procedureVersion,
                procedureStepRedlines: redlines,
                saveStatus: SaveStatus.Saved
            },
        }
    }),
    on(PrismCommonActions.EditProcedureLoadProcedureData.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.EditProcedure.updatename, (state, { name }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                name,
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.updatedescription, (state, { description }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                description,
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.createstep, (state, { step }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                steps: [...state.editProcedureState.procedureVersion.steps, step]
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.deletestep, (state, { stepId }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                steps: state.editProcedureState.procedureVersion.steps.filter(s => s.id !== stepId)
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.updatestep, (state, { step }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                steps: state.editProcedureState.procedureVersion.steps.map(s => s.id === step.id ? step : s)
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.updatesteps, (state, { steps }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                steps
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.updateprefacemarkdown, (state, { prefaceMarkdown }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion: {
                ...state.editProcedureState.procedureVersion,
                prefaceMarkdown
            }
        }
    })),
    on(PrismCommonActions.EditProcedure.updatesavestatus, (state, { saveStatus }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            saveStatus,
        }
    })),
    on(PrismCommonActions.EditProcedure.setselectedredlinepanelstepid, (state, { stepId }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            selectedRedlinePanelStepId: stepId,
        }
    })),
    on(PrismCommonActions.EditProcedure.toggleexpandredline, (state, { redlineId }): PrismCommonState => ({
        ...state,
        editProcedureState: {
            ...state.editProcedureState,
            expandedRedlineIds: state.editProcedureState.expandedRedlineIds.includes(redlineId)
                ? state.editProcedureState.expandedRedlineIds.filter(id => id !== redlineId)
                : [...state.editProcedureState.expandedRedlineIds, redlineId]
        }
    })),
    // ======================================================================
    on(PrismCommonActions.EditProcedureSetProcedureVersion.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.EditProcedureSetProcedureVersion.success, (state, { procedureVersion }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion,
            saveStatus: SaveStatus.Saved,
        }
    })),
    on(PrismCommonActions.EditProcedureSetProcedureVersion.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // ======================================================================
    on(PrismCommonActions.DeleteProcedureVersion.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.DeleteProcedureVersion.success, (state, { versionId }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureVersions: state.procedureVersions.filter(version => version.id !== versionId),
        error: null
    })),
    on(PrismCommonActions.DeleteProcedureVersion.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(PrismCommonActions.EditProcedureReleaseProcedureVersion.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.EditProcedureReleaseProcedureVersion.success, (state, { procedureVersion }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null,
        editProcedureState: {
            ...state.editProcedureState,
            procedureVersion,
            saveStatus: SaveStatus.Saved,
        }
    })),
    on(PrismCommonActions.EditProcedureReleaseProcedureVersion.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(PrismCommonActions.EditProcedureSetProcedureStepRedline.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.EditProcedureSetProcedureStepRedline.success, (state, { redline }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null,
        editProcedureState: {
            ...state.editProcedureState,
            procedureStepRedlines: MergeArrayValues(state.editProcedureState.procedureStepRedlines, redline),
        }
    })),
    on(PrismCommonActions.EditProcedureSetProcedureStepRedline.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // ======================================================================
    on(PrismCommonActions.SetProcedureStepRedline.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetProcedureStepRedline.success, (state, { redline }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureStepRedlines: MergeArrayValues(state.procedureStepRedlines, redline),
        error: null
    })),
    on(PrismCommonActions.SetProcedureStepRedline.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetScheduledProcedure.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetScheduledProcedure.success, (state, { scheduledProcedure }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        scheduledProcedures: MergeArrayValues(state.scheduledProcedures, scheduledProcedure),
        error: null
    })),
    on(PrismCommonActions.SetScheduledProcedure.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.DeleteScheduledProcedure.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.DeleteScheduledProcedure.success, (state, { scheduledProcedure }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        scheduledProcedures: state.scheduledProcedures.filter(sp => sp.id !== scheduledProcedure.id),
        error: null
    })),
    on(PrismCommonActions.DeleteScheduledProcedure.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadScheduledProcedures.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadScheduledProcedures.success, (state, { scheduledProcedures }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        scheduledProcedures,
        error: null
    })),
    on(PrismCommonActions.LoadScheduledProcedures.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadAssetHistoryEventsByAssetId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadAssetHistoryEventsByAssetId.success, (state, { assetHistoryEvents }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        assetHistoryEvents: MergeArrayValues(state.assetHistoryEvents, assetHistoryEvents),
        error: null
    })),
    on(PrismCommonActions.LoadAssetHistoryEventsByAssetId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetAssetHistoryEvent.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetAssetHistoryEvent.success, (state, { event }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        assetHistoryEvents: MergeArrayValues(state.assetHistoryEvents, event),
        error: null
    })),
    on(PrismCommonActions.SetAssetHistoryEvent.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadScheduledProceduresByProcedureId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadScheduledProceduresByProcedureId.success, (state, { scheduledProcedures }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        scheduledProcedures: MergeArrayValues(state.scheduledProcedures, scheduledProcedures),
        error: null
    })),
    on(PrismCommonActions.LoadScheduledProceduresByProcedureId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadScheduledProcedureDataByAssetId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadScheduledProcedureDataByAssetId.success, (state, {
        scheduledProcedures,
        procedures,
        procedureVersions,
    }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedures: MergeArrayValues(state.procedures, procedures),
        procedureVersions: MergeArrayValues(state.procedureVersions, procedureVersions),
        scheduledProcedures: MergeArrayValues(state.scheduledProcedures, scheduledProcedures),
        error: null
    })),
    on(PrismCommonActions.LoadScheduledProcedureDataByAssetId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetFactoryAsset.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.SetFactoryAsset.success, (state, { factoryAsset }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryAssets: MergeArrayValues(state.factoryAssets, factoryAsset),
        error: null,
    })),
    on(PrismCommonActions.SetFactoryAsset.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadFactoryAssets.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadFactoryAssets.success, (state, { factoryAssets }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryAssets,
        error: null
    })),
    on(PrismCommonActions.LoadFactoryAssets.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadFactoryAssetsByType.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadFactoryAssetsByType.success, (state, { factoryAssets }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryAssets,
        error: null
    })),
    on(PrismCommonActions.LoadFactoryAssetsByType.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadFactoryAssetById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadFactoryAssetById.success, (state, { factoryAsset }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryAssets: MergeArrayValues(state.factoryAssets, factoryAsset),
        error: null,
    })),
    on(PrismCommonActions.LoadFactoryAssetById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadResourceByFactoryAssetId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadResourceByFactoryAssetId.success, (state, { resources }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        resources,
        error: null,
    })),
    on(PrismCommonActions.LoadResourceByFactoryAssetId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadFactoryAssetsByResourceId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadFactoryAssetsByResourceId.success, (state, { factoryAssets }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryAssets,
        error: null,
    })),
    on(PrismCommonActions.LoadFactoryAssetsByResourceId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadProceduresAndReleasedVersionsByAssetId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadProceduresAndReleasedVersionsByAssetId.success, (state, { procedureVersions, procedures }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        procedureVersions,
        procedures,
        error: null,
    })),
    on(PrismCommonActions.LoadProceduresAndReleasedVersionsByAssetId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadMappingAssetToResources.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadMappingAssetToResources.success, (state, { mappingAssetToResources }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToResources,
        error: null,
    })),
    on(PrismCommonActions.LoadMappingAssetToResources.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadMappingAssetToResourcesByAssetId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadMappingAssetToResourcesByAssetId.success, (state, { mappingAssetToResources }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToResources,
        error: null,
    })),
    on(PrismCommonActions.LoadMappingAssetToResourcesByAssetId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetMappingAssetToResource.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetMappingAssetToResource.success, (state, { mappingAssetToResource }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToResources: MergeArrayValues(state.mappingAssetToResources, mappingAssetToResource),
        error: null,
    })),
    on(PrismCommonActions.SetMappingAssetToResource.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.DeleteMappingAssetToResource.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.DeleteMappingAssetToResource.success, (state, { mappingAssetToResource }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToResources: state.mappingAssetToResources.filter(mapping => mapping.id !== mappingAssetToResource.id),
        error: null,
    })),
    on(PrismCommonActions.DeleteMappingAssetToResource.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadMappingAssetToProceduresByAssetId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadMappingAssetToProceduresByAssetId.success, (state, { mappingAssetToProcedures }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToProcedures,
        error: null,
    })),
    on(PrismCommonActions.LoadMappingAssetToProceduresByAssetId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetMappingAssetToProcedure.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.SetMappingAssetToProcedure.success, (state, { mappingAssetToProcedure }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToProcedures: MergeArrayValues(state.mappingAssetToProcedures, mappingAssetToProcedure),
        error: null,
    })),
    on(PrismCommonActions.SetMappingAssetToProcedure.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.DeleteMappingAssetToProcedure.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.DeleteMappingAssetToProcedure.success, (state, { mappingAssetToProcedure }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        mappingAssetToProcedures: state.mappingAssetToProcedures.filter(mapping => mapping.id !== mappingAssetToProcedure.id),
        error: null,
    })),
    on(PrismCommonActions.DeleteMappingAssetToProcedure.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadBrowseProceduresData.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadBrowseProceduresData.success, (state, { instances, procedures, versions }): PrismCommonState => {
        return {
            ...state,
            loadingState: state.loadingState - 1,
            procedureInstances: MergeArrayValues(state.procedureInstances, instances),
            procedures: MergeArrayValues(state.procedures, procedures),
            procedureVersions: MergeArrayValues(state.procedureVersions, versions),
        }
    }),
    on(PrismCommonActions.LoadBrowseProceduresData.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetFreightCarrier.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.SetFreightCarrier.success, (state, { freightCarrier }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        freightCarriers: MergeArrayValues(state.freightCarriers, freightCarrier),
        error: null,
    })),
    on(PrismCommonActions.SetFreightCarrier.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadFreightCarriers.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadFreightCarriers.success, (state, { freightCarriers }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        freightCarriers,
        error: null,
    })),
    on(PrismCommonActions.LoadFreightCarriers.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetCapitalizationProject.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.SetCapitalizationProject.success, (state, { project }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        capitalizationProjects: MergeArrayValues(state.capitalizationProjects, project),
        error: null,
    })),
    on(PrismCommonActions.SetCapitalizationProject.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadCapitalizationProjects.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadCapitalizationProjects.success, (state, { projects }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        capitalizationProjects: projects,
        error: null,
    })),
    on(PrismCommonActions.LoadCapitalizationProjects.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadCustomers.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadCustomers.success, (state, { customers }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        customers,
        error: null
    })),
    on(PrismCommonActions.LoadCustomers.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadCustomerById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadCustomerById.success, (state, { customer }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        customers: MergeArrayValues(state.customers, customer),
        error: null
    })),
    on(PrismCommonActions.LoadCustomerById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadCustomersByIds.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadCustomersByIds.success, (state, { customers }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        customers,
        error: null
    })),
    on(PrismCommonActions.LoadCustomersByIds.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetCustomer.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetCustomer.success, (state, { customer }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        customers: MergeArrayValues(state.customers, customer),
        error: null
    })),
    on(PrismCommonActions.SetCustomer.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(
        PrismCommonActions.LoadPlasticsCustomers.initial,
        PrismCommonActions.LoadMetalsCustomers.initial,
        PrismCommonActions.LoadC3Customers.initial,
        (state): PrismCommonState => ({
            ...state,
            loadingState: state.loadingState + 1,
            error: null,
        })),
    on(
        PrismCommonActions.LoadPlasticsCustomers.success,
        PrismCommonActions.LoadMetalsCustomers.success,
        PrismCommonActions.LoadC3Customers.success,
        (state, { customers }): PrismCommonState => ({
            ...state,
            loadingState: state.loadingState - 1,
            customers,
            error: null,
        })),
    on(
        PrismCommonActions.LoadPlasticsCustomers.failure,
        PrismCommonActions.LoadMetalsCustomers.failure,
        PrismCommonActions.LoadC3Customers.failure,
        (state, { error }): PrismCommonState => ({
            ...state,
            loadingState: state.loadingState - 1,
            error,
        })),
    // =========================================================
    on(PrismCommonActions.LoadVendors.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadVendors.success, (state, { vendors }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendors,
        error: null
    })),
    on(PrismCommonActions.LoadVendors.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(
        PrismCommonActions.LoadPlasticsVendors.initial,
        PrismCommonActions.LoadMetalsVendors.initial,
        PrismCommonActions.LoadC3Vendors.initial,
        PrismCommonActions.LoadAutomationVendors.initial,
        (state): PrismCommonState => ({
            ...state,
            loadingState: state.loadingState + 1,
            error: null
        })),
    on(
        PrismCommonActions.LoadPlasticsVendors.success,
        PrismCommonActions.LoadMetalsVendors.success,
        PrismCommonActions.LoadC3Vendors.success,
        PrismCommonActions.LoadAutomationVendors.success,
        (state, { vendors }): PrismCommonState => ({
            ...state,
            loadingState: state.loadingState - 1,
            vendors,
            error: null
        })),
    on(
        PrismCommonActions.LoadPlasticsVendors.failure,
        PrismCommonActions.LoadMetalsVendors.failure,
        PrismCommonActions.LoadC3Vendors.failure,
        PrismCommonActions.LoadAutomationVendors.failure,
        (state, { error }): PrismCommonState => ({
            ...state,
            loadingState: state.loadingState - 1,
            error,
        })),
    // =========================================================
    on(PrismCommonActions.LoadVendorById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadVendorById.success, (state, { vendor }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendors: MergeArrayValues(state.vendors, vendor),
        error: null
    })),
    on(PrismCommonActions.LoadVendorById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadVendorsByIds.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadVendorsByIds.success, (state, { vendors }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendors,
        error: null
    })),
    on(PrismCommonActions.LoadVendorsByIds.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetVendor.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetVendor.success, (state, { vendor }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendors: MergeArrayValues(state.vendors, vendor),
        error: null
    })),
    on(PrismCommonActions.SetVendor.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetPurchaseRequestList.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.SetPurchaseRequestList.success, (state, { purchaseRequestList }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequestLists: MergeArrayValues(state.purchaseRequestLists, purchaseRequestList),
        error: null
    })),
    on(PrismCommonActions.SetPurchaseRequestList.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadPurchaseRequestLists.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestLists.success, (state, { purchaseRequestLists }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequestLists,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestLists.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadPurchaseRequestListById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadPurchaseRequestListById.success, (state, { purchaseRequestList }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequestLists: MergeArrayValues(state.purchaseRequestLists, purchaseRequestList),
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestListById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadPurchaseRequestListsByIds.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadPurchaseRequestListsByIds.success, (state, { purchaseRequestLists }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequestLists,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestListsByIds.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    on(PrismCommonActions.LoadPurchaseRequestListsWithFollowerId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadPurchaseRequestListsWithFollowerId.success, (state, { purchaseRequestLists }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequestLists,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestListsWithFollowerId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetPurchaseRequest.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetPurchaseRequest.success, (state, { purchaseRequest }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests: MergeArrayValues(state.purchaseRequests, purchaseRequest),
        error: null
    })),
    on(PrismCommonActions.SetPurchaseRequest.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.UpdatePurchaseRequestFiles.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.UpdatePurchaseRequestFiles.success, (state, { requestId, files }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        // patch the cached copy the same way the write patched the document — replacing the whole
        // entity here would put the rest of this browser's stale copy back into the store
        purchaseRequests: state.purchaseRequests.map((request) => request.id === requestId ? { ...request, files } : request),
        error: null
    })),
    on(PrismCommonActions.UpdatePurchaseRequestFiles.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetPurchaseRequests.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetPurchaseRequests.success, (state, { purchaseRequests }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests: MergeArrayValues(state.purchaseRequests, purchaseRequests),
        error: null
    })),
    on(PrismCommonActions.SetPurchaseRequests.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadPurchaseRequests.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequests.success, (state, { purchaseRequests }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequests.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadPurchaseRequestsByPurchaseRequestListId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestsByPurchaseRequestListId.success, (state, { purchaseRequests }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestsByPurchaseRequestListId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadPurchaseRequestById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestById.success, (state, { purchaseRequest }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests: MergeArrayValues(state.purchaseRequests, purchaseRequest),
        error: null
    })),
    on(PrismCommonActions.LoadPurchaseRequestById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadIncompletePurchaseRequests.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadIncompletePurchaseRequests.success, (state, { purchaseRequests }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests,
        error: null
    })),
    on(PrismCommonActions.LoadIncompletePurchaseRequests.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadOrderedPurchaseRequestsByHardwareId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(PrismCommonActions.LoadOrderedPurchaseRequestsByHardwareId.success, (state, { purchaseRequests }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        purchaseRequests,
        error: null,
    })),
    on(PrismCommonActions.LoadOrderedPurchaseRequestsByHardwareId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadContacts.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadContacts.success, (state, { contacts }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts,
        error: null
    })),
    on(PrismCommonActions.LoadContacts.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetContact.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetContact.success, (state, { contact }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts: MergeArrayValues(state.contacts, contact),
        error: null
    })),
    on(PrismCommonActions.SetContact.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadContactById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadContactById.success, (state, { contact }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts: MergeArrayValues(state.contacts, contact),
        error: null
    })),
    on(PrismCommonActions.LoadContactById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadContactAssociationByCustomerId.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadContactAssociationByCustomerId.success, (state, { contacts }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts,
        error: null
    })),
    on(PrismCommonActions.LoadContactAssociationByCustomerId.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadSoftwareRequests.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadSoftwareRequests.success, (state, { requests }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        softwareRequests: requests,
        error: null
    })),
    on(PrismCommonActions.LoadSoftwareRequests.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.LoadSoftwareRequestById.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.LoadSoftwareRequestById.success, (state, { request }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        softwareRequests: [request],
        error: null
    })),
    on(PrismCommonActions.LoadSoftwareRequestById.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(PrismCommonActions.SetSoftwareRequest.initial, (state): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(PrismCommonActions.SetSoftwareRequest.success, (state, { request }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        softwareRequests: MergeArrayValues(state.softwareRequests, request),
        error: null
    })),
    on(PrismCommonActions.SetSoftwareRequest.failure, (state, { error }): PrismCommonState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================


    // =========================================================
    // ACTIONS FROM OTHER STATE SLICES
    // =========================================================
    on(PlasticsActions.LoadProductionManagerData.success, (state, { customers }): PrismCommonState => ({
        ...state,
        customers,
    })),
    on(PlasticsActions.LoadViewComplaintLogData.success, (state, { customer }): PrismCommonState => ({
        ...state,
        customers: MergeArrayValues(state.customers, customer)
    })),
    on(PlasticsActions.LoadBrowseComplaintLogData.success, (state, { customers }): PrismCommonState => ({
        ...state,
        customers
    })),
    on(PlasticsActions.LoadManageMaterialMixingTasksData.success, (state, { customers }): PrismCommonState => ({
        ...state,
        customers
    })),
    on(PlasticsActions.LoadViewOrderData.success, (state, { customers }): PrismCommonState => ({
        ...state,
        customers
    })),
    // =========================================================

)